/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect } from 'react';
import s from './styles.module.scss';
import { GetStaticProps, NextLayoutComponentType } from 'next';
import { IReciter } from '@/infrastructure/interfaces/i-reciter';
import { Api } from '@/api';
import { useTranslation } from 'next-i18next';
import MainLayout from '@/layouts/main';
import AppContainer from '@/components/layout/container';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import { localeList } from '@/public/locales/locale-list';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import RecitersList from '@/components/reciters/list';
import AppTitle from '@/components/common/title';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import { routes } from '@/infrastructure/const/routes';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import Head from 'next/head';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import { useRouter } from 'next/router';
import { addDomainForReciterImages } from '@/utils/helpers/add-domain-for-reciter-images';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';

const api = Api.getInstance();
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;

interface IRecitersPageProps {
  reciters: IReciter[];
}

const RecitersPage: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<IRecitersPageProps>(data);
  const { reciters } = decrypted;

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const { t: tMeta } = useTranslation('meta');
  const { t: tCommon } = useTranslation('common');
  const { t: tLinks } = useTranslation('links');

  const router = useRouter();
  const { locale } = router;

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('allReciters'),
      path: routes.reciters,
    },
  ];

  const canonicalURL =
    `${SITE_URL}${getLocaleForUrl(locale)}` + routes.reciters;

  const shema = baseMarkupSchema([
    webPageMarkupSchema(
      routes.reciters,
      tMeta('recitersPageTitle'),
      tMeta('recitersPageDescription')
    ),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    breadcrumbListMarkupSchema(breadcrumbs),
  ]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);

  return (
    <>
      <Head>
        <title>{tMeta('recitersPageTitle')}</title>
        <meta name="description" content={tMeta('recitersPageDescription')} />
        <link rel="canonical" href={canonicalURL} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(shema) }}
        />
      </Head>
      <main>
        <AppContainer>
          <AppTitle title={tCommon('allReciters')} main />
          <RecitersList
            className={s['list']}
            items={sortArrayByFieldName(reciters, 'order')}
          />
        </AppContainer>
      </main>
    </>
  );
};

RecitersPage.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  try {
    const { data: recitersResult } = await api.getReciters(
      allRecordsPaginationOptions,
      ['image', 'slug', 'title_en', 'title_ar', 'order']
    );
    const { results: reciters } = recitersResult;

    reciters.map((reciter) => {
      addDomainForReciterImages(reciter);
    });

    const data = { reciters };
    const { encryptPageProps } = await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(data);

    return {
      props: {
        data: encrypted,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default RecitersPage;
